import type { PoolEntryDto, PoolResponse } from '@zinc/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { ScoreChip } from '../../components/ScoreChip';
import { Thumbnail } from '../../components/Thumbnail';
import { api } from '../../lib/api';
import { Bar, Panel, tinyButton } from './parts';

/** Admin → Pool: the generated puzzles waiting to be served, per category; look at one larger
 * and remove it before anyone sees it. */
export function AdminPoolPage() {
  const client = useQueryClient();
  const pool = useQuery({
    queryKey: ['admin', 'pool'],
    queryFn: () => api<PoolResponse>('/api/admin/pool'),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api<null>(`/api/admin/pool/${id}`, { method: 'DELETE' }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['admin'] }),
  });
  const [open, setOpen] = useState<string | null>(null);
  const d = pool.data;
  const byCategory = (key: string): PoolEntryDto[] =>
    (d?.entries ?? []).filter((e) => e.category === key);

  return (
    <>
      <h1 className="m-0 text-[26px] font-bold">Pool</h1>
      <p className="m-0 text-[14px] text-muted">
        Generated puzzles waiting to be served. One enters the library when someone takes it with
        Generate or it becomes a daily ({d?.served ?? 0} served so far).
      </p>
      {(d?.categories ?? [])
        .filter((c) => c.enabled || c.count > 0)
        .map((c) => (
          <Panel
            key={c.key}
            title={c.name}
            side={
              <span className="text-[13px] text-muted tabular-nums">
                {c.count}/{c.size}
              </span>
            }
          >
            <Bar value={c.size ? c.count / c.size : 0} />
            {byCategory(c.key).length === 0 ? (
              <span className="text-[13px] text-muted">Empty: the nightly refill fills it.</span>
            ) : (
              <ul className="m-0 flex list-none flex-col p-0">
                {byCategory(c.key).map((e) => (
                  <li
                    key={e.id}
                    className="flex flex-wrap items-center gap-3 border-b border-line-soft py-2 text-[14px] last:border-b-0"
                  >
                    <button
                      type="button"
                      aria-label={`${open === e.id ? 'Smaller' : 'Larger'} preview of ${e.title}`}
                      onClick={() => setOpen(open === e.id ? null : e.id)}
                      className="border-0 bg-transparent p-0"
                    >
                      <Thumbnail doc={e.preview} size={open === e.id ? 280 : 48} />
                    </button>
                    <span className="min-w-0 flex-1 truncate font-semibold">{e.title}</span>
                    <span className="text-[13px] text-muted tabular-nums">
                      quality {e.quality.toFixed(2)}
                    </span>
                    <ScoreChip score={e.score} />
                    <button
                      type="button"
                      className={tinyButton}
                      disabled={remove.isPending}
                      onClick={() => remove.mutate(e.id)}
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        ))}
    </>
  );
}
